import { Link } from "react-router-dom";
import { MapPin, Maximize, BedDouble, Bath } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export const PropertyCard = ({ property }) => {
  const formatPrice = (price, type) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(price) + (type === 'location' ? '/mois' : '');
  };
  
  const getCategoryLabel = (category) => {
    const labels = {
      maison: 'Maison',
      appartement: 'Appartement',
      terrain: 'Terrain',
      garage: 'Garage',
      parking: 'Parking',
      commercial: 'Local commercial'
    };
    return labels[category] || category;
  };
  
  return (
    <Link 
      to={`/bien/${property.id}`} 
      className="property-card group block"
      data-testid={`property-card-${property.id}`}
    >
      {/* Image */}
      <div className="aspect-[4/3] relative overflow-hidden bg-gray-100">
        <img
          src={property.images?.[0] || '/placeholder-bien.svg'}
          alt={property.title}
          className="property-image"
          loading="lazy"
        />
        <div className="property-overlay" />
        
        {/* Badges */}
        <div className="absolute top-4 left-4 flex gap-2 z-10">
          <Badge 
            className={
              property.property_type === 'vente' ? 'badge-vente' : 
              property.property_type === 'location' ? 'badge-location' :
              property.property_type === 'vendu' ? 'bg-green-500 text-white border-0' :
              'bg-purple-500 text-white border-0'
            }
            data-testid={`property-type-badge-${property.id}`}
          >
            {property.property_type === 'vente' ? 'À vendre' : 
             property.property_type === 'location' ? 'À louer' :
             property.property_type === 'vendu' ? 'Vendu' : 'Loué'}
          </Badge>
          {property.is_exclusive && (
            <Badge className="bg-white text-[#0A0A0A] border border-[#D4AF37] uppercase tracking-wider" data-testid="property-card-exclusive">
              Exclusivité
            </Badge>
          )}
          {property.is_featured && (
            <Badge className="bg-[#D4AF37] text-[#0A0A0A] border-0">
              Coup de cœur
            </Badge>
          )}
        </div>
        
        {/* Price on hover */}
        <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
          <p className="text-white text-sm uppercase tracking-wider mb-1">
            {getCategoryLabel(property.category)}
          </p>
          <p className="font-serif text-2xl text-[#FBF5B7]">
            {formatPrice(property.price, property.property_type)}
          </p>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-5 space-y-3">
        {property.reference && (
          <p className="text-xs uppercase tracking-wider text-[#B8952B] font-semibold" data-testid="property-card-reference">Réf. {property.reference}</p>
        )}
        <h3 className="font-serif text-xl text-[#0A0A0A] group-hover:text-[#D4AF37] transition-colors line-clamp-1">
          {property.title}
        </h3>
        
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <MapPin size={14} className="text-[#D4AF37]" />
          <span>{property.city}, {property.postal_code}</span>
        </div>
        
        {/* Features */}
        <div className="flex items-center gap-4 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-1.5 text-gray-600 text-sm">
            <Maximize size={14} />
            <span>{property.surface} m²</span>
          </div>
          {property.bedrooms > 0 && (
            <div className="flex items-center gap-1.5 text-gray-600 text-sm">
              <BedDouble size={14} />
              <span>{property.bedrooms} ch.</span>
            </div>
          )}
          {property.bathrooms > 0 && (
            <div className="flex items-center gap-1.5 text-gray-600 text-sm">
              <Bath size={14} />
              <span>{property.bathrooms} sdb.</span>
            </div>
          )}
        </div>
        
        {/* Price (visible by default) */}
        <p className="price-tag group-hover:text-[#AA771C] transition-colors">
          {formatPrice(property.price, property.property_type)}
        </p>
      </div>
    </Link>
  );
};

export default PropertyCard;
